(function () {
  const SECTION_SELECTOR = ".fer-history";
  const PIN_SELECTOR = ".fer-history__pin";
  const VIEWPORT_SELECTOR = ".fer-history__viewport";
  const TRACK_SELECTOR = ".fer-history__track";
  const PANEL_SELECTOR = ".fer-history__panel";
  const YEAR_SELECTOR = ".fer-history__timeline .fer-history__year";
  const PROGRESS_SELECTOR = ".fer-history__timeline-progress";
  const TIMELINE_YEARS_SELECTOR = ".fer-history__timeline-years";
  const TIMELINE_TRACK_SELECTOR = ".fer-history__timeline-track";
  const DESKTOP_MQ = window.matchMedia("(min-width: 901px)");
  const REDUCED_MOTION_MQ = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );

  const instances = new WeakMap();

  function getScrollDistance(viewport, track) {
    return Math.max(0, track.scrollWidth - viewport.clientWidth);
  }

  function getYearsScrollDistance(yearsViewport, yearsTrack) {
    return Math.max(0, yearsTrack.scrollWidth - yearsViewport.clientWidth);
  }

  function getActivePanel(viewport, track, panels) {
    if (!panels.length) return null;

    const isDesktopTrack = DESKTOP_MQ.matches && !REDUCED_MOTION_MQ.matches;
    let scrollOffset = 0;

    if (isDesktopTrack && typeof gsap !== "undefined") {
      scrollOffset = Math.abs(gsap.getProperty(track, "x") || 0);
    } else {
      scrollOffset = viewport.scrollLeft;
    }

    const focusX = scrollOffset + viewport.clientWidth * 0.35;
    let activePanel = panels[0];

    panels.forEach((panel) => {
      const panelStart = panel.offsetLeft;
      const panelEnd = panelStart + panel.offsetWidth;
      if (focusX >= panelStart) activePanel = panel;
      if (focusX >= panelEnd) activePanel = panel;
    });

    return activePanel;
  }

  function getScrollProgress(viewport, track) {
    const distance = getScrollDistance(viewport, track);
    if (distance <= 0) return 0;

    const isDesktopTrack = DESKTOP_MQ.matches && !REDUCED_MOTION_MQ.matches;
    const offset = isDesktopTrack
      ? Math.abs(gsap.getProperty(track, "x") || 0)
      : viewport.scrollLeft;

    return Math.min(1, Math.max(0, offset / distance));
  }

  function updateYearsScrollMode(section) {
    const yearsViewport = section.querySelector(TIMELINE_YEARS_SELECTOR);
    const yearsTrack = section.querySelector(TIMELINE_TRACK_SELECTOR);
    if (!yearsViewport || !yearsTrack) return;

    const isScrollable = getYearsScrollDistance(yearsViewport, yearsTrack) > 1;
    yearsViewport.classList.toggle("is-scrollable", isScrollable);
  }

  function syncYearsHorizontalScroll(section, viewport, track) {
    const yearsViewport = section.querySelector(TIMELINE_YEARS_SELECTOR);
    const yearsTrack = section.querySelector(TIMELINE_TRACK_SELECTOR);
    if (!yearsViewport || !yearsTrack) return;

    updateYearsScrollMode(section);

    const yearsDistance = getYearsScrollDistance(yearsViewport, yearsTrack);
    if (yearsDistance <= 0) {
      yearsViewport.scrollLeft = 0;
      return;
    }

    const padding = 24;
    const progress = getScrollProgress(viewport, track);
    let scrollLeft = progress * yearsDistance;

    const activeYear = section.querySelector(`${YEAR_SELECTOR}.is-active`);
    if (activeYear) {
      const viewportWidth = yearsViewport.clientWidth;
      const yearStart = activeYear.offsetLeft;
      const yearEnd = yearStart + activeYear.offsetWidth;
      const visibleStart = scrollLeft;
      const visibleEnd = scrollLeft + viewportWidth;

      if (yearStart < visibleStart + padding) {
        scrollLeft = yearStart - padding;
      } else if (yearEnd > visibleEnd - padding) {
        scrollLeft = yearEnd - viewportWidth + padding;
      }
    }

    yearsViewport.scrollLeft = Math.min(yearsDistance, Math.max(0, scrollLeft));
  }

  function setTimelineState(section, viewport, track) {
    const panels = section.querySelectorAll(PANEL_SELECTOR);
    const years = [...section.querySelectorAll(YEAR_SELECTOR)];
    const progressEl = section.querySelector(PROGRESS_SELECTOR);
    const activePanel = getActivePanel(viewport, track, panels);
    const activeYear = activePanel?.dataset.year || "";
    const activeYearIndex = years.findIndex(
      (yearEl) => yearEl.dataset.year === activeYear,
    );

    years.forEach((yearEl, index) => {
      const isActive = yearEl.dataset.year === activeYear;
      yearEl.classList.toggle("is-active", isActive);
      yearEl.classList.toggle(
        "is-passed",
        activeYearIndex > -1 && index < activeYearIndex,
      );
    });

    if (progressEl) {
      progressEl.style.width = `${getScrollProgress(viewport, track) * 100}%`;
    }

    syncYearsHorizontalScroll(section, viewport, track);
  }

  function destroyHistory(section) {
    const instance = instances.get(section);
    if (!instance) return;

    instance.scrollTrigger?.kill();
    instance.tween?.kill();
    instance.viewportScrollHandler?.();
    instances.delete(section);

    const track = section.querySelector(TRACK_SELECTOR);
    if (track) gsap.set(track, { clearProps: "transform" });

    section.querySelectorAll(YEAR_SELECTOR).forEach((yearEl) => {
      yearEl.classList.remove("is-active", "is-passed");
    });

    const progressEl = section.querySelector(PROGRESS_SELECTOR);
    if (progressEl) progressEl.style.width = "0";

    const yearsViewport = section.querySelector(TIMELINE_YEARS_SELECTOR);
    if (yearsViewport) {
      yearsViewport.scrollLeft = 0;
      yearsViewport.classList.remove("is-scrollable");
    }
  }

  function initHistory(section) {
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined")
      return;

    const pin = section.querySelector(PIN_SELECTOR);
    const viewport = section.querySelector(VIEWPORT_SELECTOR);
    const track = section.querySelector(TRACK_SELECTOR);

    destroyHistory(section);

    if (!pin || !viewport || !track) return;

    const syncTimeline = () => setTimelineState(section, viewport, track);

    const scrollDistance = getScrollDistance(viewport, track);
    const shouldAnimate =
      DESKTOP_MQ.matches && !REDUCED_MOTION_MQ.matches && scrollDistance > 0;

    if (shouldAnimate) {
      gsap.registerPlugin(ScrollTrigger);

      const tween = gsap.to(track, {
        x: () => -getScrollDistance(viewport, track),
        ease: "none",
        scrollTrigger: {
          trigger: pin,
          pin: true,
          scrub: true,
          start: "top top",
          end: () => "+=" + getScrollDistance(viewport, track),
          invalidateOnRefresh: true,
          anticipatePin: 1,
          onUpdate: syncTimeline,
        },
      });

      instances.set(section, {
        tween,
        scrollTrigger: tween.scrollTrigger,
      });
    } else {
      const onViewportScroll = () => syncTimeline();
      viewport.addEventListener("scroll", onViewportScroll, { passive: true });

      instances.set(section, {
        viewportScrollHandler: () => {
          viewport.removeEventListener("scroll", onViewportScroll);
        },
      });
    }

    updateYearsScrollMode(section);
    syncTimeline();

    track.querySelectorAll("img").forEach((img) => {
      if (img.complete) return;
      img.addEventListener(
        "load",
        () => {
          updateYearsScrollMode(section);
          setTimelineState(section, viewport, track);
          ScrollTrigger?.refresh();
        },
        { once: true },
      );
    });
  }

  function bindHistory(section) {
    if (section.dataset.ferHistoryBound) return;
    section.dataset.ferHistoryBound = "true";

    const refresh = () => {
      destroyHistory(section);
      initHistory(section);
      if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
    };

    DESKTOP_MQ.addEventListener("change", refresh);
    REDUCED_MOTION_MQ.addEventListener("change", refresh);
    window.addEventListener("resize", refresh);

    refresh();
  }

  function initHistorySections(root) {
    const scope = root && root.querySelector ? root : document;
    scope.querySelectorAll(SECTION_SELECTOR).forEach(bindHistory);
  }

  function waitForGsap(callback) {
    if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined") {
      callback();
      return;
    }

    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined") {
        window.clearInterval(interval);
        callback();
      } else if (attempts >= 50) {
        window.clearInterval(interval);
      }
    }, 100);
  }

  function boot(root) {
    waitForGsap(() => initHistorySections(root));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (event) => {
    boot(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll(SECTION_SELECTOR).forEach(destroyHistory);
  });
})();
